/* eslint-disable i18next/no-literal-string */
import { useEffect, useState, type RefObject } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { useHasBeenOnScreen } from "~/lib/use-has-been-on-screen";

/**
 * Ported from bouncieweb's `LandingPageComponents/DrivingHistoryCarousel.js`.
 *
 * Three stacked "trip" cards that cycle. Each card gets one of three position
 * classes — `next` (small, up top), `current` (front and center), `previous`
 * (small, below) — and CSS transitions animate the swap. Every 2.5s the order
 * rotates (front card moves to the back) up to 3 times, then stops. The cycle
 * starts once the parent bento item has scrolled into view (`parentRef` +
 * `useHasBeenOnScreen`, passed down from HighlightsGrid).
 *
 * The three states are reproduced with data-attributes + a scoped <style> so
 * the exact transforms/offsets from the original survive.
 */

const transitionInterval = 2.5 * 1000;
const transitionCountMax = 3;

type Trip = {
  distance: string;
  start: { time: string; place: string };
  end: { time: string; place: string };
};

const trips: Trip[] = [
  {
    distance: "21.2 mi",
    start: { time: "3:53 PM", place: "27933 Jacobson Lake" },
    end: { time: "5:14 PM", place: "10930 Lucie Meadow" },
  },
  {
    distance: "25.5 mi",
    start: { time: "8:37 AM", place: "10930 Lucie Meadow" },
    end: { time: "9:12 AM", place: "4021 Hampton Shores" },
  },
  {
    distance: "10.5 mi",
    start: { time: "7:52 PM", place: "4021 Hampton Shores" },
    end: { time: "8:13 PM", place: "27933 Jacobson Lake" },
  },
];

function TripCard({ trip, order }: { trip: Trip; order: string }) {
  return (
    <div className="cf-dh-trip" data-order={order}>
      {/* Connector */}
      <div
        className="absolute top-[30%] left-[1.56em] h-[40%] w-px"
        style={{ background: colors.grayLight1 }}
      />
      {/* TripMilestones */}
      <div className="flex flex-col gap-[0.6em]">
        <div className="flex items-center gap-[0.7em]">
          <span
            className="relative h-[0.6em] w-[0.6em] rounded-full"
            style={{ background: colors.green }}
          />
          <span className="w-[4em] text-left">{trip.start.time}</span>
          <div>{trip.start.place}</div>
        </div>
        <div className="flex items-center gap-[0.7em]">
          <span
            className="relative h-[0.6em] w-[0.6em] rounded-full"
            style={{ background: colors.red }}
          />
          <span className="w-[4em] text-left">{trip.end.time}</span>
          <div>{trip.end.place}</div>
        </div>
      </div>
      {/* Distance */}
      <div className="[font-family:var(--font-sans)] font-bold">
        {trip.distance}
      </div>
    </div>
  );
}

export default function DrivingHistoryCarousel({
  parentRef,
}: {
  parentRef: RefObject<Element | null>;
}) {
  const hasBeenOnScreen = useHasBeenOnScreen(parentRef);
  const [, setTransitionCount] = useState(0);
  const [orderClasses, setOrderClasses] = useState([
    "next",
    "current",
    "previous",
  ]);

  useEffect(() => {
    if (!hasBeenOnScreen) return;
    const id = setInterval(() => {
      setOrderClasses((old) => [...old.slice(1), old[0]]);
      setTransitionCount((prev) => {
        if (prev === transitionCountMax - 1) clearInterval(id);
        return prev + 1;
      });
    }, transitionInterval);
    return () => clearInterval(id);
  }, [hasBeenOnScreen]);

  return (
    <>
      <style>{`
        .cf-dh-list {
          display: inline-block;
          position: relative;
          height: 12rem;
          width: 100%;
          font-size: 3vw;
        }
        @media (min-width: 431px) {
          .cf-dh-list { width: 21.5rem; font-size: 14px; }
        }
        @media (min-width: 1024px) {
          .cf-dh-list { width: 100%; margin-top: -1rem; font-size: 10.5px; }
        }
        @media (min-width: 1280px) {
          .cf-dh-list { margin-top: -0.5rem; font-size: 11.75px; }
        }
        .cf-dh-trip {
          background: white;
          padding: 1.3em;
          border-radius: 0.5rem;
          box-shadow: 0 5px 10px rgba(0,0,0,.2);
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-wrap: nowrap;
          position: absolute;
          top: 0;
          width: 100%;
          transition: top 0.8s ease-out, transform 0.8s ease-out, background 0.8s ease-out;
        }
        .cf-dh-trip > * { transition: opacity 0.5s ease-out; }
        .cf-dh-trip[data-order="next"] {
          transform: scale(0.9);
          background: #ECF2FC;
          top: 1rem;
          z-index: 0;
        }
        .cf-dh-trip[data-order="next"] > * { opacity: 0.5; }
        .cf-dh-trip[data-order="current"] {
          top: 5.2em;
          z-index: 2;
        }
        .cf-dh-trip[data-order="current"] > * { opacity: 1; }
        .cf-dh-trip[data-order="previous"] {
          transform: scale(0.9);
          background: #ECF2FC;
          z-index: 1;
          top: 9em;
        }
        .cf-dh-trip[data-order="previous"] > * { opacity: 0.5; }
      `}</style>

      <div className={cn("cf-dh-list")}>
        {trips.map((trip, i) => (
          <TripCard key={i} trip={trip} order={orderClasses[i]} />
        ))}
      </div>
    </>
  );
}
